import React, { FC, useState, useCallback } from "react";
import { AxiosInstance } from "axios";
import { reverse, sortBy } from "ramda";

import { IReceiptsContext, PaginationInfo, ReceiptsContext } from "./index";
import { ReceiptInfo } from "src/views/ReceiptsForm/types";
import { RequestStatus } from "../api/types";
import { toQueryParms } from "src/utils";

export const ReceiptsProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [receipts, setReceipts] = useState<ReceiptInfo[] | null>(null);
  const [receiptsStatus, setReceiptsStatus] = useState<RequestStatus>("idle");
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);

  const searchReceipts = useCallback(
    async (
      api: AxiosInstance,
      searchParams: Record<string, string>
    ): Promise<ReceiptInfo[]> => {
      setReceiptsStatus("loading");
      try {
        const response = await api.get(
          `api/receipts?${toQueryParms(searchParams)}&limit=10&offset=0`
        );

        const { data: receiptsData, pagination: newPagination } = response.data;
        const sortedReceipts = reverse(
          sortBy((r: ReceiptInfo) => r.draftCreatedAt || "", receiptsData)
        ) as ReceiptInfo[];
        setReceipts(sortedReceipts);
        setPagination(newPagination);
        setReceiptsStatus("success");
        return sortedReceipts;
      } catch (err) {
        setReceiptsStatus("error");
        throw err;
      }
    },
    []
  );

  const loadMoreReceipts = useCallback(
    async (api: AxiosInstance, searchParams: Record<string, string>) => {
      if (!pagination || !pagination.hasMore) return;

      try {
        const newOffset = pagination.offset + pagination.limit;
        const response = await api.get(
          `api/receipts?${toQueryParms(searchParams)}&limit=${
            pagination.limit
          }&offset=${newOffset}`
        );

        const { data: newReceipts, pagination: newPagination } = response.data;

        setReceipts([...(receipts || []), ...newReceipts]);
        setPagination(newPagination);
      } catch (err) {
        console.error("Failed to load more receipts:", err);
        throw err;
      }
    },
    [pagination, receipts]
  );

  const receiptsContext: IReceiptsContext = {
    receipts,
    setReceipts,
    receiptsStatus,
    setReceiptsStatus,
    pagination,
    setPagination,
    searchReceipts,
    loadMoreReceipts,
  };

  return (
    <ReceiptsContext.Provider value={receiptsContext}>
      {children}
    </ReceiptsContext.Provider>
  );
};

export const useReceiptsActions = (api: AxiosInstance) => {
  const { searchReceipts, loadMoreReceipts } =
    React.useContext(ReceiptsContext);

  const searchReceiptsWithApi = React.useCallback(
    (searchParams: Record<string, string>) => searchReceipts(api, searchParams),
    [api, searchReceipts]
  );

  const loadMoreReceiptsWithApi = React.useCallback(
    (searchParams: Record<string, string>) =>
      loadMoreReceipts(api, searchParams),
    [api, loadMoreReceipts]
  );

  return {
    searchReceipts: searchReceiptsWithApi,
    loadMoreReceipts: loadMoreReceiptsWithApi,
  };
};
